import { objectsEqual, arraysEqual } from '@utils/performance';
import {
  calcBMSMSavings,
  getTieredSubtotal,
  calculateSWD,
  formatMoneyCents,
} from '@utils/price-helpers';
export const getters = {
  getLineItems: (state) => state.items,
  getLineInCart: (state) => {
    const items = state.items;
    return (item) => {
      const id = item.id;
      const variant = item.variant_id;
      const properties = item.properties ?? {};
      const components = item.components ?? [];

      const line = items.findIndex((item) => {
        const hasMatchingId = item.id === id;
        const hasMatchingVariant = item.variant_id === variant;
        const hasMatchingProps = objectsEqual(item.properties, properties);
        const hasMatchingComponents = arraysEqual(item.components, components);

        return (
          hasMatchingId &&
          hasMatchingVariant &&
          hasMatchingProps &&
          hasMatchingComponents
        );
      });

      return line;
    };
  },
  getShipping: (state) => {
    const { terms, free_ship_thresh } = state;
    const thresh = free_ship_thresh / 100;
    const eligibleSubtotal = state.eligibleItemsSubtotal;
    const exceedsMinimumThreshold = eligibleSubtotal >= thresh;

    if (exceedsMinimumThreshold) {
      return terms.shipping.free;
    } else {
      return terms.shipping.tbd;
    }
  },

  hasNote: (state) => {
    const note = state.note.length > 0 ?? false;
    const giftNote = state?.gift_note ?? false;
    const items = state.getLineItems;
    const ids = items.map((item) => item.variant_id);
    return note && ids.includes(giftNote);
  },
  totePresentInCart: (state) => {
    const items = state.getLineItems;
    const tote = state?.global?.tote ?? false;
    if (!tote) return true;

    const variant = tote?.env[state.mode]?.variant ?? false;
    if (!variant) return true;

    const variantIds = items.map((item) => item.variant_id);
    return variantIds.includes(variant);
  },
  getIneligibleItems: (state) => {
    const ids = [];
    const global = window?.Brooklinen ?? {};
    const mode = global?.mode ?? 'production';

    const tote = state?.global?.tote ?? false;
    const giftNote = state?.gift_note ?? false;

    if (tote) {
      ids.push(tote.env[mode].variant);
    }

    if (giftNote) {
      ids.push(giftNote);
    }

    return ids;
  },
  hasOrphanedBundleItems: (state) => {
    // we can define a 'broken cart' as a cart that does not contain a "bundle" shell product yet contains products that have bundle line item properties.

    //find the first instance of a bundle component item in the cart, and through its line item properties, get the shell product
    const bundleToFind = state.items.find(
      (item) => item.properties?._product_bundle_title
    );
    const bundleProductTitle = bundleToFind?.properties?._product_bundle_title;

    //check if the aforementioned shell product exists within the cart. If the product does not exist yet there are products with bundle line item properties in the cart, it is a broken cart.
    const hasMatchingBundleTitle = state.items.some((item) =>
      item.title.includes(bundleProductTitle)
    );

    const hasBundleVariant = state.items.some(
      (item) => item.properties?._bundle_variantId
    );

    return !hasMatchingBundleTitle && hasBundleVariant;
  },
  eligibleItemsSubtotal: (state) => {
    const items = state.getLineItems;
    const { swd_tags } = state;

    const eligibleItems = items.filter((item) => {
      const notFreeGift = !item.tags.includes('Free Gift');
      const notDropShipped = !item.tags.includes('Dropship');
      const notGiftCard = !item.product_type.includes('Gift Card');
      return notFreeGift && notGiftCard && notDropShipped;
    });

    const containsBrooklinen =
      eligibleItems.map((item) => item.vendor).length > 0;

    /*
     If there is a broken cart, recalculate the subtotal using the sum of all line items. Then, send error reporting to heap
    The subtotal for a bundle is 1 dollar whenever the cart breaks. There are also instances where the subtotal breaks while the cart is still intact,
    such as adding 10x of a bundle to your cart via quantity input. We will only send a heap event when the cart breaks, but we will need to recalculate the subtotal in
    any event where the subtotal is incorrect (i.e. a price that's less than 10 dollars)
    */
    if (state.subtotal < 10) {
      if (state.hasOrphanedBundleItems) {
        const brokenBundle = state.items.find(
          (item) => item.properties?._product_bundle_title
        )?.properties?._product_bundle_title;
        const orphanedComponents = state.items.filter(
          (item) => item.properties?._product_bundle_title
        )?.length;

        window?.heap?.track('Custom Theme Event - Cart Break', {
          bundle: brokenBundle,
          brokenCartComponents: orphanedComponents,
        });
      }

      const brokenCartSubtotal = state.items.reduce((subtotal, item) => {
        return item.price + subtotal;
      }, 0);
      return brokenCartSubtotal;
    }

    if (containsBrooklinen && state.tieredDiscount.enabled)
      return state.subtotal;

    const eligibleSubtotal = eligibleItems.reduce((subtotal, item) => {
      const isBundle = item?.is_bundle ?? false;
      const isCompare = item?.compareAtPrice > 1;
      const allocatedDiscount =
        item.price == item.fromPrice.value
          ? item.line_level_total_discount ?? 0
          : 0;
      const eligible = item.tags.some((tag) => swd_tags.includes(tag));
      let sum;

      if (isBundle) {
        const hasBundleDiscount =
          (item.bundle_discount && item.bundle_discount > 0) ?? 0;
        const bundleDiscount = hasBundleDiscount
          ? 1 - item.bundle_discount / 100
          : 0;
        sum = item.components.reduce((total, item) => {
          const componentPrice = item.component_price;
          const quantity = item.quantity;
          const amount = componentPrice * bundleDiscount * quantity;
          total += amount;
          return total;
        }, 0);
      } else {
        //when calculating subtotal, use the original price of the item in order to properly apply SWDs
        //note that this logic does not apply for free gift with purchase items
        //in the case of last call items, discount the item beforehand with the last call discount
        let itemPrice = item.price == 0 ? item.price : item.fromPrice.value;

        if (isCompare && item.price != item.fromPrice.value) {
          itemPrice *= (100 - item.bundle_discount) / 100;
        }
        sum = itemPrice * item.quantity + allocatedDiscount;
      }

      //checks if shopify has already discounted the items via SWD to prevent a double application of the SWD
      if (item.bmsm_enabled) {
        //checks if shopify functions has already discounted the items via BMSM tag structure
        sum *= calcBMSMSavings(item);
      }
      // Calculates SWD for eligible items
      const price = calculateSWD(sum, { swd: eligible });
      subtotal += price;
      return subtotal;
    }, 0);

    return formatMoneyCents(eligibleSubtotal);
  },
  upsellIsReady: (state) => {
    const items = state.upsell.items ?? [];
    const loaded = state.upsell.loaded ?? false;
    const hasItems = items.length > 0;

    return loaded && hasItems;
  },
  isGiftWithPurcase: () => {
    return (item) => {
      const properties = item?.properties ?? {};
      return Object.keys(properties).includes('_gift-with-purchase');
    };
  },
  gwpCriteriaMet: (state) => {
    if (!state?.gwp) return false;

    const itemInCart = state?.gwp?.added ?? false;
    const currentlyActive = state?.gwp?.active ?? false;

    return !itemInCart && currentlyActive;
  },
  getTieredSubtotal: (state) => getTieredSubtotal(state.getLineItems),
  tieredDiscountsEnabled: (state) => state.tieredDiscount.enabled,
};
